namespace $ {

	/** Repeatable fiber */
	export class $mol_wire_atom<
		Host,
		Args extends readonly unknown[],
		Result,
	> extends $mol_wire_fiber< Host, Args, Result > {

		static solo<
			Host,
			Args extends readonly unknown[],
			Result,
		>(
			host: Host,
			task: ( this: Host, ... args: Args )=> Result,
		): $mol_wire_atom< Host, Args, Result > {
			
			const field = task.name + '()'
			
			const existen = Object.getOwnPropertyDescriptor( host ?? task, field )?.value
			if( existen ) return existen
			
			const prefix = host?.[ Symbol.toStringTag ] ?? ( host instanceof Function ? $$.$mol_func_name( host ) : host )
			const key = `${ prefix }.${ field }`
			
			const fiber = new $mol_wire_atom( key, task, host, [] as any as Args )
			;( host ?? task )[ field ] = fiber
			
			return fiber
		}
		
		static plex<
			Host,
			Args extends readonly unknown[],
			Result,
		>(
			host: Host,
			task: ( this: Host , ... args: Args )=> Result,
			key: Args[0],
		): $mol_wire_atom< Host, Args, Result > {
			
			const field = task.name + '()'
			let dict = Object.getOwnPropertyDescriptor( host ?? task, field )?.value
			const prefix = host?.[ Symbol.toStringTag ] ?? ( host instanceof Function ? $$.$mol_func_name( host ) : host )
			const id = `${ prefix }.${ task.name }(${ $mol_key( key ) })`
			
			if( dict ) {
				const existen = dict.get( id )
				if( existen ) return existen
			} else {
				dict = ( host ?? task )[ field ] = new Map<any,any>()
			}
			
			const fiber = new $mol_wire_atom( id, task, host, [ key ] as any as Args )
			dict.set( id, fiber )
			
			return fiber
		}
		
		static watching = new Set< $mol_wire_atom< any, any, any > >()
		static watcher = null as $mol_after_frame | null
		
		static watch() {
		
			$mol_wire_atom.watcher = new $mol_after_frame( $mol_wire_atom.watch )
			
			for( const atom of $mol_wire_atom.watching ) {
				
				if( atom.cursor === $mol_wire_cursor.final ) {
					$mol_wire_atom.watching.delete( atom )
				} else {
					atom.cursor = $mol_wire_cursor.stale
					atom.fresh()
				}
				
			}
			
		}
		
		watch() {
			
			if( !$mol_wire_atom.watcher ) {
				$mol_wire_atom.watcher = new $mol_after_frame( $mol_wire_atom.watch )
			}
			
			$mol_wire_atom.watching.add( this )
			
		}
		
		/**
		 * Update fiber value through another temp fiber.
		 */
		@ $mol_wire_method
		resync( args: Args ) {
			return this.put( this.task.call( this.host!, ... args ) )
		}
		
		@ $mol_wire_method
		once() {
			return this.sync()
		}
		
		channel() {
			return Object.assign( ( next?: $mol_type_foot< Args > )=> {
					
				if( next !== undefined ) return this.resync( [ ... this.args, next ] as any ) as never
				if( !$mol_wire_fiber.warm ) return this.result() as never
				
				if( $mol_wire_auto() instanceof $mol_wire_task ) {
					return this.once()
				} else {
					return this.sync()
				}
				
			}, { atom: this as $mol_wire_atom< Host, Args, Result > } )
		}
		
		destructor() {
			
			super.destructor()
			
			const prev = this.cache
			if( $mol_owning_check( this, prev ) ) {
				prev.destructor()
			}
			
			if( this.pub_from === 0 ) {
				;( this.host ?? this.task )[ this.field() ] = null
			} else {
				;( this.host ?? this.task )[ this.field() ].delete( this[ Symbol.toStringTag ] )
			}
			
		}
		
		put( next: Result | Error | Promise< Result | Error > ) {
			
			const prev = this.cache
			
			update: if( next !== prev ) {
				
				try {
					if( $mol_compare_deep( prev, next ) ) break update
				} catch( error: any ) {
					$mol_fail_log( error )
				}
				
				if( $mol_owning_check( this, prev ) ) {
					prev.destructor()
				}
				
				if( $mol_owning_catch( this, next ) ) {
					try {
						next[ Symbol.toStringTag ] = this[ Symbol.toStringTag ]
					} catch { // Promises throws in strict mode
						Object.defineProperty( next, Symbol.toStringTag, { value: this[ Symbol.toStringTag ] } )
					}
				}
				
				if( !this.sub_empty ) this.emit()
				
			}
			
			this.cache = next
			this.cursor = $mol_wire_cursor.fresh
			
			if( next instanceof Promise ) return next
			
			this.complete_pubs()
			
			return next
		}
		
	}
	
	// $mol_wire_atom.watch()
	
}
